import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import apiService from "../../../../../../service/Api";
import { showErrorToast, showSuccessToast } from "../../../../../../components/common/ShowToast";

interface PaymentReminderDialogProps {
  open: boolean;
  bookingId?: number;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  onClose: () => void;
}

interface PaymentReminderResponse {
  detail?: string;
}

export default function PaymentReminderDialog({
  open,
  bookingId,
  clientName,
  clientEmail,
  clientPhone,
  onClose,
}: PaymentReminderDialogProps) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState<"email" | "whatsapp" | null>(null);

  useEffect(() => {
    if (open) {
      setMessage(
        `Hello ${clientName || "there"},\n\nThis is a gentle reminder that payment for your completed booking is still outstanding. Please let us know if you need any payment details or assistance.\n\nThank you,\nMuzukuru Funeral Streaming`,
      );
    }
  }, [open, clientName]);

  const send = async (channel: "email" | "whatsapp") => {
    if (!bookingId) return;
    setSending(channel);
    try {
      const response = await apiService.post<PaymentReminderResponse>(
        `/api/bookings/booking/${bookingId}/send-client-payment-reminder/`,
        { channel, message },
      );
      showSuccessToast(response.data?.detail || "Payment reminder sent.");
      onClose();
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      showErrorToast(detail || "Unable to send the payment reminder.");
    } finally {
      setSending(null);
    }
  };

  const hasEmail = Boolean(clientEmail && clientEmail !== "N/A");
  const hasPhone = Boolean(clientPhone && clientPhone !== "N/A");

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send payment reminder</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Review the message, then choose how to contact {clientName || "the client"}.
        </Typography>
        {!hasEmail && !hasPhone && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This client has no email address or WhatsApp number.
          </Alert>
        )}
        <TextField
          label="Message"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          multiline
          rows={7}
          fullWidth
          disabled={Boolean(sending)}
        />
        <Box mt={1}>
          {hasEmail && <Typography variant="caption">Email: {clientEmail}</Typography>}
          {hasEmail && hasPhone && <Typography variant="caption"> · </Typography>}
          {hasPhone && <Typography variant="caption">WhatsApp: {clientPhone}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={Boolean(sending)}>Cancel</Button>
        <Button
          variant="outlined"
          startIcon={<EmailOutlinedIcon />}
          disabled={!hasEmail || Boolean(sending)}
          onClick={() => send("email")}
        >
          {sending === "email" ? "Sending…" : "Send email"}
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<WhatsAppIcon />}
          disabled={!hasPhone || Boolean(sending)}
          onClick={() => send("whatsapp")}
        >
          {sending === "whatsapp" ? "Sending…" : "Send WhatsApp"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
